import { find, set } from "lodash";
import { DecisionGraph } from "./graph-utils/DecisionGraph";
import { getNodeResult } from "./graph-utils/GraphMisc";
import type { GraphNode } from "./graph-utils/GraphTypes";
import type { Relationship } from "./CommonTypes";

export interface GeneratePayloadOptions {
  /**
   * Build the inputs from the goal's direct children rather than walking down to the leaf input
   * nodes (used when testing a single rule in the graph).
   */
  useChildren?: boolean;
  /** Value written for each input. Defaults to the node's input/derived result, or `null`. */
  getValue?: (node: GraphNode) => unknown;
}

export interface GeneratedPayload {
  mode: "batch";
  data: Record<string, any>;
}

const defaultGetValue = (node: GraphNode) => {
  const val = getNodeResult(node);
  return typeof val === "undefined" ? null : val;
};

/**
 * Build an example decision API batch request for a goal: every input the goal depends on, keyed
 * by id (or description), nested under the entity instances needed to reach it from global.
 * Returns `{}` when the goal or release is missing, the goal isn't a string, the graph can't be
 * parsed, or the goal isn't in the graph.
 */
export const generatePayload = (
  goal: string,
  useDesc: boolean,
  release: { relationships?: Relationship[] } | undefined,
  rawGraph: any,
  options: GeneratePayloadOptions = {},
): GeneratedPayload | Record<string, never> => {
  const { useChildren = false, getValue = defaultGetValue } = options;
  // Untyped JS callers can pass a non-string goal (e.g. `{}`), which getNode can't look up
  if (!goal || typeof goal !== "string" || !release) return {};
  let graph: DecisionGraph;
  try {
    graph = DecisionGraph.from(rawGraph);
  } catch (error) {
    return {};
  }
  const root = graph.node(graph.find(goal)) as GraphNode | undefined;
  if (!root) return {};
  // a node's rule node (by its attribute id): the goal and inputs are attributes, whatever instance the goal was found at
  const ruleNodeId = (node: GraphNode) => graph.ref(node.id);
  const nodesOf = (ids: Iterable<number>) =>
    [...ids].map((c) => graph.node(c) as GraphNode | undefined).filter((x): x is GraphNode => !!x);
  let inputs: GraphNode[] = [];
  if (useChildren) {
    const rootId = ruleNodeId(root);
    // Filter in the unlikely event a node doesn't generate properly
    if (rootId !== undefined) inputs = nodesOf(graph.successors(rootId));
  } else {
    // Find all the input nodes for this goal
    const seen = new Set<string>();
    const nodeLoop = (node: GraphNode) => {
      if (seen.has(node.id)) return;

      seen.add(node.id);

      const id = ruleNodeId(node);
      const children = id === undefined ? [] : nodesOf(graph.successors(id));
      if (children.length > 0) {
        for (const child of children) nodeLoop(child);
      } else {
        // No children - I'm likely an input
        // TODO: edge case for predefined nodes
        if (node.value || node.conditions || node.rows) return;

        inputs.push(node);
      }
    };
    nodeLoop(root);
  }

  const request: GeneratedPayload = {
    mode: "batch",
    data: {},
  };
  const added: string[] = [];
  // Array paths throughout: an entity/relationship name is a canonical id and can legally contain
  // a '.', so a string path (re-split on '.' by lodash) would corrupt it.
  const recurseToGlobal = (entity: string, rel: Relationship, incomplete: string[]): string[] | false => {
    if (rel.source === "global") {
      const relName = rel.name as string;
      if (added.indexOf(relName) === -1) {
        // We haven't added this rel yet
        request.data[relName] = [{ "@id": "1" }];
        added.push(relName);
      }

      let path: string[] = ["data", relName, "0"];
      for (let i = incomplete.length - 1; i >= 0; i--) {
        if (added.indexOf(incomplete[i]) === -1) {
          // We haven't added this yet
          set(request, [...path, incomplete[i]], [{ "@id": "1" }]);
          added.push(incomplete[i]);
        }

        path = [...path, incomplete[i], "0"];
      }

      if (added.indexOf(entity) === -1) {
        set(request, [...path, entity], [{ "@id": "1" }]);
        added.push(entity);
      }
      if (relName !== entity) path = [...path, entity, "0"];

      return path;
    }

    // I'm at a sub-entity
    const parent = find(release.relationships, { target: rel.source });
    // error
    if (!parent) return false;
    if (entity !== rel.name) incomplete.push(rel.name as string);

    return recurseToGlobal(entity, parent, incomplete);
  };
  const getPathToGlobal = (node: GraphNode): string[] | false => {
    const rel = find(release.relationships, { name: node.entity });
    // error - can't find relationship
    if (!rel) return false;

    return recurseToGlobal(node.entity as string, rel, []);
  };
  for (const node of inputs) {
    const id = (useDesc ? node.description : node.id) as string;
    const example = getValue(node);
    if (node.entity === "global") {
      set(request, ["data", id], example);
    } else {
      // Connected to an entity
      const path = getPathToGlobal(node);
      set(request, [...(path || []), id], example);
    }
  }
  return request;
};
